import produce from 'immer'
import { createAction } from 'redux-actions'
import { useRef } from 'react'
import { v4 } from 'uuid'
import { identity } from '../utils'

export const DEFAULT_NAMESPACE = '__base__'

/**
 * Wraps an action creator such that the first param is the namespace
 */
export const createNamespacedAction = (type, payloadCreator = identity, metaCreator) => createAction(
  // type as usual
  type,

  // shift off ns for the normal payload creator
  (ns, ...args) => payloadCreator(...args),

  // wrap possible meta creator, merging in the ns with default
  (ns = DEFAULT_NAMESPACE, ...args) => {
    const meta = metaCreator ? metaCreator(...args) : {}
    return { ...meta, ns }
  }
)

/**
 * Wraps a reducer in a higher order reducer that reduces to a slice of the state
 * selected by namespace
 *
 * Returning `undefined` from the reducer will result in deleting the namespaced slice
 * from the state.  NOTE on immer here, as `return undefined` from produce is not possible,
 * delete from the state you'd need to return immer's `nothing`.
 *
 * See: https://immerjs.github.io/immer/docs/return#producing-undefined-using-nothing
 */
export const createNamespacedReducer = reducer => produce((draft, action) => {
  const ns = action?.meta?.ns || DEFAULT_NAMESPACE
  const slice = reducer(draft[ns], action)

  if (slice === undefined) {
    delete draft[ns]
  } else {
    draft[ns] = slice
  }
})

/**
 * Creates a selector creator that selects on a namespace
 */
export const createNamespacedSelector = selector => (ns = DEFAULT_NAMESPACE) => state => {
  return selector(state)?.[ns]
}

export function useNamespace(ns) {
  const fallbackUuid = useRef(v4())
  return ns || fallbackUuid.current
}
